/**
 * nav-config.ts
 *
 * Navigation configuration for the LawKit marketing header.
 * Ported from shell.jsx NAV_CONFIG (lines 43-71).
 *
 * Route corrections vs prototype:
 *   prototype /pricing → actual route /plan (spec §5.1)
 *   prototype /about-lawkit → actual route /introduce (spec §5.8)
 *   prototype /about-firm → actual members route /company (spec §5.9)
 */

import { MEDIA_SECTIONS } from '@/lib/media-sections';
import { PLAN_ANCHOR, planSectionHref } from '@/components/marketing/data/pricing';

export interface NavSupportItem {
  to: string;
  label: string;
  sub: string;
}

export interface NavItemBase {
  key: string;
  label: string;
  /**
   * 최상위 라벨이 가리키는 허브 라우트.
   *
   * 메가 패널이 달린 항목도 라벨 자체는 항상 허브로 이동한다 — 패널을 여는 일은
   * hover/focus 와 라벨 옆 캐럿 버튼이 맡는다(HeaderClient.tsx 참고).
   */
  to: string;
}

export interface NavItemLink extends NavItemBase {
  type: 'link';
}

/** Side blurb shown on the left of a mega panel. */
export interface NavMegaSide {
  /** Eyebrow label, e.g. "SERVICES" */
  title: string;
  /** One-paragraph description */
  blurb: string;
  /** Side CTA — `to` for internal route, `href` for in-page anchor */
  cta: { to?: string; href?: string; label: string };
}

export interface NavItemMega extends NavItemBase {
  type: 'mega';
  side: NavMegaSide;
}

export interface NavItemMegaCompact extends NavItemBase {
  type: 'mega-compact';
  side: NavMegaSide;
  items: NavSupportItem[];
}

export type NavItem = NavItemLink | NavItemMega | NavItemMegaCompact;

export const NAV_CONFIG: NavItem[] = [
  {
    key: 'services',
    label: '서비스',
    to: '/services',
    type: 'mega',
    side: {
      title: 'SERVICES',
      blurb: '법무법인 오킴스 기업자문팀이 운영하는 5가지 핵심 서비스입니다. 단건·멤버쉽으로 모두 이용 가능합니다.',
      cta: { to: '/plan', label: '요금 및 계약 안내' },
    },
  },
  {
    key: 'plan',
    label: '요금 및 계약 안내',
    to: '/plan',
    type: 'mega-compact',
    side: {
      title: 'PRICING',
      blurb: '단건·단체·월 멤버십 — 사안 크기와 거래 빈도에 맞춰 계약 방식을 선택하세요.',
      cta: { to: '/plan', label: '요금 및 계약 안내 전체 보기' },
    },
    items: [
      // 세 항목 모두 같은 페이지의 다른 구획이다 — 요금 안내는 2026-09-15 부터 한 장이다.
      { to: planSectionHref(PLAN_ANCHOR.priceGuide), label: '단건 계약', sub: '필요한 서비스 하나만, 빠르게' },
      // 번들 계약은 우선 노출 보류 (주석 처리). 복원 시 함께 활성화할 것.
      // { to: '/plan#bundle', label: '번들 계약', sub: '자주 쓰는 조합을 묶음가로' },
      { to: planSectionHref(PLAN_ANCHOR.group), label: '단체 계약', sub: '단체 단위 상시 자문' },
      // 크레딧은 시간이 아니라 금액(원)이다 — utils/membership.ts, 요금표의 멤버십 구획과 같은 말.
      { to: planSectionHref(PLAN_ANCHOR.membership), label: '월 멤버십', sub: '금액 크레딧 기반 Retainer' },
    ],
  },
  {
    key: 'media',
    label: '미디어 센터',
    to: '/media',
    type: 'mega-compact',
    side: {
      title: 'MEDIA CENTER',
      blurb: '법률 인사이트, 전문가 오피니언, 업무사례, 최신 소식과 로킷프렌즈를 한 곳에서 만나보세요.',
      cta: { href: '/media', label: '미디어 센터 전체 보기' },
    },
    items: MEDIA_SECTIONS.map(section => ({
      to: `/media/${section.slug}`,
      label: section.label,
      sub: section.englishLabel,
    })),
  },
  {
    key: 'support',
    label: '고객 지원',
    // 고객 지원만 전용 허브 라우트가 없다 — 패널 첫 항목이자 실질적 진입점인 /faq 로 보낸다.
    to: '/faq',
    type: 'mega-compact',
    side: {
      title: 'CUSTOMER SUPPORT',
      blurb: 'LawKit과 법무법인 오킴스 기업자문팀에 대해 더 자세히 알아보세요.',
      cta: { href: '#inquiry', label: '상담 신청' },
    },
    items: [
      { to: '/faq', label: 'FAQ', sub: '자주 묻는 질문' },
      { to: '/introduce', label: 'LawKit 소개', sub: '서비스 운영 방식' },
      { to: '/company', label: 'Team LawKit', sub: '법무법인 오킴스 기업자문팀 & 글로벌 비즈니스 센터' },
      { to: '/partners', label: '협력사', sub: 'LawKit과 함께하는 기업·기관' },
    ],
  },
];
